import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixLoyaltyReferralV1ReferralEvent from './loyalty-referral-v1-referral-event-tracker.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface ReferralEvent extends ReferralEventEventTypeOneOf {
  /** Event triggered when a referred friend signs up. */
  referredFriendSignupEvent?: ReferredFriendSignupEvent;
  /** Event triggered when a referral is successful. For example, customer places and pays for an order. */
  successfulReferralEvent?: V1SuccessfulReferralEvent;
  /** Event triggered when an action is performed. For example, placing an order. */
  actionEvent?: V1ActionEvent;
  /** Event triggered when a reward is given. */
  rewardEvent?: RewardEvent;
  /**
   * Referral event ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the referral event is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the referral event.
   * @immutable
   */
  revision?: string | null;
  /**
   * Date and time the referral event was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the referral event was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

/** @oneof */
export interface ReferralEventEventTypeOneOf {
  /** Event triggered when a referred friend signs up. */
  referredFriendSignupEvent?: ReferredFriendSignupEvent;
  /** Event triggered when a referral is successful. For example, customer places and pays for an order. */
  successfulReferralEvent?: V1SuccessfulReferralEvent;
  /** Event triggered when an action is performed. For example, placing an order. */
  actionEvent?: V1ActionEvent;
  /** Event triggered when a reward is given. */
  rewardEvent?: RewardEvent;
}

export interface ReferredFriendSignupEvent {
  /**
   * ID of the referred friend.
   * @format GUID
   */
  referredFriendId?: string;
}

export interface V1SuccessfulReferralEvent {
  /**
   * ID of the referred friend.
   * @format GUID
   */
  referredFriendId?: string;
  /**
   * ID of the referring customer.
   * @format GUID
   */
  referringCustomerId?: string;
}

export interface V1ActionEvent {
  /**
   * ID of the referred friend.
   * @format GUID
   */
  referredFriendId?: string;
  /**
   * ID of the referring customer.
   * @format GUID
   */
  referringCustomerId?: string;
  /** Trigger for the action. */
  trigger?: V1Trigger;
  /** Amount associated with the action. */
  amount?: string | null;
  /** Currency of the amount. */
  currency?: string | null;
  /** ID of the associated order. */
  orderId?: string | null;
}

export interface V1Trigger {
  /** ID of the app that triggered the event. */
  appId?: string;
  /** Type of activity that triggered the event. */
  activityType?: string;
}

export interface RewardEvent extends RewardEventReceiverOneOf {
  /**
   * ID of the rewarded referring customer.
   * @format GUID
   * @readonly
   */
  rewardedReferringCustomerId?: string;
  /**
   * ID of the rewarded referred friend.
   * @format GUID
   * @readonly
   */
  rewardedReferredFriendId?: string;
  /**
   * ID of the referral reward.
   * @format GUID
   */
  referralRewardId?: string;
  /** Type of reward. */
  rewardType?: RewardWithLiterals;
}

/** @oneof */
export interface RewardEventReceiverOneOf {
  /**
   * ID of the rewarded referring customer.
   * @format GUID
   * @readonly
   */
  rewardedReferringCustomerId?: string;
  /**
   * ID of the rewarded referred friend.
   * @format GUID
   * @readonly
   */
  rewardedReferredFriendId?: string;
}

export enum Reward {
  /** Unknown reward. This field is not used. */
  UNKNOWN = 'UNKNOWN',
  /** Reward is a coupon. */
  COUPON = 'COUPON',
  /** Reward is loyalty points. */
  LOYALTY_POINTS = 'LOYALTY_POINTS',
  /** No reward. */
  NOTHING = 'NOTHING',
}

/** @enumType */
export type RewardWithLiterals =
  | Reward
  | 'UNKNOWN'
  | 'COUPON'
  | 'LOYALTY_POINTS'
  | 'NOTHING';

export interface CreateReferralEventRequest {
  /** Referral event to create. */
  referralEvent?: ReferralEvent;
}

export interface CreateReferralEventResponse {
  /** Created referral event. */
  referralEvent?: ReferralEvent;
}

export interface GetReferralEventRequest {
  /**
   * ID of the referral event to retrieve.
   * @format GUID
   */
  referralEventId: string;
}

export interface GetReferralEventResponse {
  /** Retrieved referral event. */
  referralEvent?: ReferralEvent;
}

export interface QueryReferralEventRequest {
  /** Query to filter referral events. */
  query: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryReferralEventResponse {
  /** List of referral events. */
  referralEvents?: ReferralEvent[];
  /** Metadata for the paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface GetReferralStatisticsRequest {}

export interface GetReferralStatisticsResponse {
  /** Total number of sign-ups completed by referred friends. */
  totalSignUpsCompleted?: number;
  /** Total number of actions completed by referred friends. */
  totalActionsCompleted?: number;
  /** Total amount of purchases made by referred friends. */
  totalAmountGenerated?: string;
}

export interface QueryReferringCustomerTotalsRequest {
  /** Query to filter referring customer totals. */
  query?: CursorQuery;
  /**
   * List of contact IDs to filter referring customer totals.
   * @format GUID
   */
  contactIds?: string[];
}

export interface QueryReferringCustomerTotalsResponse {
  referringCustomerTotals?: ReferringCustomerTotal[];
  /** Paging metadata. */
  metadata?: CursorPagingMetadata;
}

export interface ReferringCustomerTotal {
  /**
   * ID of the referring customer.
   * @format GUID
   * @readonly
   */
  referringCustomerId?: string;
  /**
   * Contact ID.
   * @format GUID
   * @readonly
   */
  contactId?: string;
  /**
   * Date and time of the last successful referral.
   * @readonly
   */
  lastSuccessfulReferral?: Date | null;
  /**
   * Total number of successful referrals made by this customer.
   * @readonly
   */
  totalSuccessfulReferrals?: number;
  /**
   * Total amount of revenue generated by friends referred by this customer.
   * @readonly
   */
  totalAmountGenerated?: string;
  /**
   * Date and time of the last friend action.
   * @readonly
   */
  lastFriendAction?: Date | null;
  /**
   * Number of friends who have completed actions.
   * @readonly
   */
  totalFriendsWithActions?: number;
}

export interface QueryReferredFriendActionsRequest {
  /** Query to filter referred friend actions. */
  query?: CursorQuery;
  /**
   * List of contact IDs to filter referred friend actions.
   * @format GUID
   */
  contactIds?: string[];
}

export interface QueryReferredFriendActionsResponse {
  /** List of referred friend actions matching the query. */
  referredFriendActions?: ReferredFriendAction[];
  /** Paging metadata. */
  metadata?: CursorPagingMetadata;
}

export interface ReferredFriendAction
  extends ReferredFriendActionRewardTypeOptionsOneOf {
  /** Coupon reward type options. */
  coupon?: V1Coupon;
  /** Loyalty points reward type options. */
  loyaltyPoints?: LoyaltyPoints;
  /**
   * Referred friend ID.
   * @format GUID
   * @readonly
   */
  referredFriendId?: string;
  /**
   * Contact ID.
   * @format GUID
   * @readonly
   */
  contactId?: string;
  /**
   * Trigger for the first action.
   * @readonly
   */
  trigger?: V1Trigger;
  /**
   * Date and time of the first action.
   * @readonly
   */
  actionDate?: Date | null;
  /** Type of issued reward. */
  rewardType?: RewardWithLiterals;
  /** Number of actions completed. */
  totalActions?: number;
  /**
   * Total amount spent by this referred friend.
   * @readonly
   */
  totalAmountSpent?: string;
  /**
   * Date and time of friend signup.
   * @readonly
   */
  signupDate?: Date | null;
}

/** @oneof */
export interface ReferredFriendActionRewardTypeOptionsOneOf {
  /** Coupon reward type options. */
  coupon?: V1Coupon;
  /** Loyalty points reward type options. */
  loyaltyPoints?: LoyaltyPoints;
}

export interface V1Coupon {
  /**
   * Coupon ID. Example: `8934b045-7052-4a90-be2b-832c70afc9da`.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * The code that customers can use to apply the coupon. Example: `6RFD2A3HSPXW`.
   * @readonly
   */
  code?: string;
  /**
   * Current status of the coupon.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Detailed specifications of the coupon.
   * @readonly
   */
  couponSpecification?: Coupon;
}

export enum Status {
  /** Coupon status is unknown or not specified. */
  UNKNOWN = 'UNKNOWN',
  /** Coupon is active and can be applied to purchases. */
  ACTIVE = 'ACTIVE',
  /** Coupon was applied and can't be used again. */
  APPLIED = 'APPLIED',
  /** Coupon was deleted and is no longer valid. */
  DELETED = 'DELETED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'UNKNOWN'
  | 'ACTIVE'
  | 'APPLIED'
  | 'DELETED';

export interface Coupon
  extends CouponDiscountTypeOptionsOneOf,
    CouponScopeOrMinSubtotalOneOf {
  /** Options for fixed amount discount. */
  fixedAmountOptions?: FixedAmountDiscount;
  /** Options for percentage discounts. */
  percentageOptions?: PercentageDiscount;
  /** Limit the coupon to carts with a subtotal above this number. */
  minimumSubtotal?: number;
  /** Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values). */
  scope?: CouponScope;
  /**
   * Coupon name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /** Coupon discount type. */
  discountType?: DiscountTypeWithLiterals;
  /**
   * Whether the coupon is limited to one item.
   * If `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.
   * Coupons with a bookings `scope.namespace` are always limited to one item.
   */
  limitedToOneItem?: boolean | null;
  /** Whether the coupon applies to subscription products. */
  appliesToSubscriptions?: boolean | null;
  /**
   * Specifies the amount of discounted cycles for a subscription item.
   *
   * - Can only be set when `scope.namespace = pricingPlans`.
   * - If `discountedCycleCount` is empty, the coupon applies to all available cycles.
   * - `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.
   *
   * Max: `999`
   */
  discountedCycleCount?: number | null;
}

/** @oneof */
export interface CouponDiscountTypeOptionsOneOf {
  /** Options for fixed amount discount. */
  fixedAmountOptions?: FixedAmountDiscount;
  /** Options for percentage discounts. */
  percentageOptions?: PercentageDiscount;
}

/** @oneof */
export interface CouponScopeOrMinSubtotalOneOf {
  /** Limit the coupon to carts with a subtotal above this number. */
  minimumSubtotal?: number;
  /** Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values). */
  scope?: CouponScope;
}

export enum DiscountType {
  /** Unknown discount type. */
  UNKNOWN = 'UNKNOWN',
  /** Discount as a fixed amount. */
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  /** Discount as a percentage. */
  PERCENTAGE = 'PERCENTAGE',
  /** Free shipping. If `true`, the coupon applies to all items in all `namespaces`. */
  FREE_SHIPPING = 'FREE_SHIPPING',
}

/** @enumType */
export type DiscountTypeWithLiterals =
  | DiscountType
  | 'UNKNOWN'
  | 'FIXED_AMOUNT'
  | 'PERCENTAGE'
  | 'FREE_SHIPPING';

export interface FixedAmountDiscount {
  /**
   * Amount of the discount as a fixed value.
   * @min 0.01
   */
  amount?: number;
}

export interface PercentageDiscount {
  /**
   * Percentage of discount.
   * @max 100
   */
  percentage?: number;
}

export interface CouponScope {
  /** Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans) */
  namespace?: string;
  /** Coupon scope's applied group, for example, Event or ticket in Wix Events. */
  group?: Group;
}

export interface Group {
  /** Name of the group. */
  name?: string;
  /** Entity ID of the group. */
  entityId?: string | null;
}

export interface LoyaltyPoints {
  /**
   * Loyalty transaction ID.
   * @format GUID
   * @readonly
   */
  transactionId?: string;
  /**
   * The number of loyalty points awarded.
   * @readonly
   */
  amount?: number;
}

export interface ReprocessReferralEventsRequest
  extends ReprocessReferralEventsRequestEntityOneOf {
  /**
   * ID of the referring customer.
   * @format GUID
   */
  referringCustomerId?: string;
  /**
   * ID of the referred friend.
   * @format GUID
   */
  referredFriendId?: string;
}

/** @oneof */
export interface ReprocessReferralEventsRequestEntityOneOf {
  /**
   * ID of the referring customer.
   * @format GUID
   */
  referringCustomerId?: string;
  /**
   * ID of the referred friend.
   * @format GUID
   */
  referredFriendId?: string;
}

export interface ReprocessReferralEventsResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface SuccessfulReferralEvent {
  /** Details of the referred friend who completed their referral. */
  referredFriendDetails?: ReferredFriendDetails;
}

export interface ReferredFriendDetails {
  /**
   * ID of the referred friend.
   * @format GUID
   * @readonly
   */
  referredFriendId?: string;
  /**
   * Contact ID of the referred friend.
   * @format GUID
   * @readonly
   */
  contactId?: string;
  /**
   * ID of the customer who referred this friend.
   * @format GUID
   * @readonly
   */
  referringCustomerId?: string;
}

export interface ReferredFriendActionEvent {
  /** Details of the referred friend. */
  referredFriendDetails?: ReferredFriendDetails;
  /** Details of the trigger. */
  trigger?: Trigger;
  /** Amount of the referral reward. */
  amount?: string | null;
  /** Currency of the referral reward. */
  currency?: string | null;
  /** ID of the order associated with the referral. */
  orderId?: string | null;
}

export interface Trigger {
  /** ID of the app associated with the referral activity. */
  appId?: string;
  /** Type of referral activity. */
  activityType?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ReferralEventCreatedEnvelope {
  entity: ReferralEvent;
  metadata: EventMetadata;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage Referrals
 * @permissionScopeId SCOPE.DC-REFERRALS.MANAGE-REFERRALS
 * @permissionId REFERRALS.READ_REFERRAL_EVENTS
 * @webhook
 * @eventType wix.loyalty.referral.v1.referral_event_created
 * @serviceIdentifier wix.loyalty.referral.tracker.v1.ReferralEvents
 * @slug created
 */
export declare function onReferralEventCreated(
  handler: (event: ReferralEventCreatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves a referral event by ID.
 * @param referralEventId - ID of the referral event to retrieve.
 * @public
 * @requiredField referralEventId
 * @permissionId REFERRALS.READ_REFERRAL_EVENTS
 * @applicableIdentity APP
 * @returns Retrieved referral event.
 * @fqn wix.loyalty.referral.tracker.v1.ReferralEvents.GetReferralEvent
 */
export async function getReferralEvent(
  referralEventId: string
): Promise<
  NonNullablePaths<
    ReferralEvent,
    | `referredFriendSignupEvent.referredFriendId`
    | `successfulReferralEvent.referredFriendId`
    | `successfulReferralEvent.referringCustomerId`
    | `actionEvent.referredFriendId`
    | `actionEvent.referringCustomerId`
    | `actionEvent.trigger.appId`
    | `actionEvent.trigger.activityType`
    | `rewardEvent.rewardedReferringCustomerId`
    | `rewardEvent.rewardedReferredFriendId`
    | `rewardEvent.referralRewardId`
    | `rewardEvent.rewardType`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    referralEventId: referralEventId,
  });

  const reqOpts =
    ambassadorWixLoyaltyReferralV1ReferralEvent.getReferralEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.referralEvent!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { referralEventId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['referralEventId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of referral events.
 *
 * The `queryReferralEvents()` function builds a query to retrieve a list of referral events and returns a `ReferralEventsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `ReferralEventsQueryBuilder` functions onto the query. `ReferralEventsQueryBuilder` functions enable you to sort, filter, and control the results `queryReferralEvents()` returns.
 *
 * `queryReferralEvents()` runs with these `ReferralEventsQueryBuilder` defaults, which you can override:
 *
 * - skip(0)
 * - limit(50)
 * - descending("_createdDate")
 *
 * The functions that are chained to `queryReferralEvents()` are applied in the order they're called. For example, if you apply ascending('actionEvent') and then descending('rewardEvent'), the results are sorted first by the action events, and then, if there are multiple results with the same action events, the items are sorted by reward events.
 * @public
 * @permissionId REFERRALS.READ_REFERRAL_EVENTS
 * @applicableIdentity APP
 * @fqn wix.loyalty.referral.tracker.v1.ReferralEvents.QueryReferralEvent
 */
export function queryReferralEvent(): ReferralEventsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    ReferralEvent,
    'CURSOR',
    QueryReferralEventRequest,
    QueryReferralEventResponse
  >({
    func: async (payload: QueryReferralEventRequest) => {
      const reqOpts =
        ambassadorWixLoyaltyReferralV1ReferralEvent.queryReferralEvent(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryReferralEventRequest['query']) => {
      const args = [query, {}] as [QueryReferralEventRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryReferralEventResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.referralEvents,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ReferralEventsQueryResult extends QueryCursorResult {
  items: ReferralEvent[];
  query: ReferralEventsQueryBuilder;
  next: () => Promise<ReferralEventsQueryResult>;
  prev: () => Promise<ReferralEventsQueryResult>;
}

export interface ReferralEventsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | 'referredFriendSignupEvent'
      | 'successfulReferralEvent'
      | 'actionEvent'
      | 'rewardEvent'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ReferralEventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | 'referredFriendSignupEvent'
      | 'successfulReferralEvent'
      | 'actionEvent'
      | 'rewardEvent'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ReferralEventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => ReferralEventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => ReferralEventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => ReferralEventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => ReferralEventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | 'referredFriendSignupEvent'
      | 'successfulReferralEvent'
      | 'actionEvent'
      | 'rewardEvent'
      | '_createdDate'
      | '_updatedDate',
    value: any[]
  ) => ReferralEventsQueryBuilder;
  in: (
    propertyName:
      | 'referredFriendSignupEvent'
      | 'successfulReferralEvent'
      | 'actionEvent'
      | 'rewardEvent'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ReferralEventsQueryBuilder;
  exists: (
    propertyName:
      | 'referredFriendSignupEvent'
      | 'successfulReferralEvent'
      | 'actionEvent'
      | 'rewardEvent'
      | '_createdDate'
      | '_updatedDate',
    value: boolean
  ) => ReferralEventsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | 'referredFriendSignupEvent'
      | 'successfulReferralEvent'
      | 'actionEvent'
      | 'rewardEvent'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => ReferralEventsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | 'referredFriendSignupEvent'
      | 'successfulReferralEvent'
      | 'actionEvent'
      | 'rewardEvent'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => ReferralEventsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => ReferralEventsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => ReferralEventsQueryBuilder;
  find: () => Promise<ReferralEventsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.loyalty.referral.tracker.v1.ReferralEvents.QueryReferralEvent
 * @requiredField query
 */
export async function typedQueryReferralEvent(
  query: ReferralEventQuery
): Promise<
  NonNullablePaths<
    QueryReferralEventResponse,
    | `referralEvents`
    | `referralEvents.${number}.referredFriendSignupEvent.referredFriendId`
    | `referralEvents.${number}.successfulReferralEvent.referredFriendId`
    | `referralEvents.${number}.successfulReferralEvent.referringCustomerId`
    | `referralEvents.${number}.actionEvent.referredFriendId`
    | `referralEvents.${number}.actionEvent.referringCustomerId`
    | `referralEvents.${number}.actionEvent.trigger.appId`
    | `referralEvents.${number}.actionEvent.trigger.activityType`
    | `referralEvents.${number}.rewardEvent.rewardedReferringCustomerId`
    | `referralEvents.${number}.rewardEvent.rewardedReferredFriendId`
    | `referralEvents.${number}.rewardEvent.referralRewardId`
    | `referralEvents.${number}.rewardEvent.rewardType`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixLoyaltyReferralV1ReferralEvent.queryReferralEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ReferralEventQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_updatedDate',
        'actionEvent',
        'referredFriendSignupEvent',
        'rewardEvent',
        'successfulReferralEvent'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  ReferralEvent,
  ReferralEventQuerySpec
>;
export type ReferralEventQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      ReferralEvent,
      ReferralEventQuerySpec,
      ReferralEventQuery
    >(),
  },
};

/**
 * Retrieves referral statistics.
 * @public
 * @permissionId REFERRALS.READ_REFERRAL_STATISTICS
 * @applicableIdentity APP
 * @fqn wix.loyalty.referral.tracker.v1.ReferralEvents.GetReferralStatistics
 */
export async function getReferralStatistics(): Promise<
  NonNullablePaths<
    GetReferralStatisticsResponse,
    `totalSignUpsCompleted` | `totalActionsCompleted` | `totalAmountGenerated`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixLoyaltyReferralV1ReferralEvent.getReferralStatistics(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of referring customer totals, given the provided paging, filtering, and sorting.
 *
 * To learn about working with Query endpoints, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
 * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/pagination),
 * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
 * @public
 * @permissionId REFERRALS.READ_REFERRAL_STATISTICS
 * @applicableIdentity APP
 * @fqn wix.loyalty.referral.tracker.v1.ReferralEvents.QueryReferringCustomerTotals
 */
export async function queryReferringCustomerTotals(
  options?: QueryReferringCustomerTotalsOptions
): Promise<
  NonNullablePaths<
    QueryReferringCustomerTotalsResponse,
    | `referringCustomerTotals`
    | `referringCustomerTotals.${number}.referringCustomerId`
    | `referringCustomerTotals.${number}.contactId`
    | `referringCustomerTotals.${number}.totalSuccessfulReferrals`
    | `referringCustomerTotals.${number}.totalAmountGenerated`
    | `referringCustomerTotals.${number}.totalFriendsWithActions`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: options?.query,
    contactIds: options?.contactIds,
  });

  const reqOpts =
    ambassadorWixLoyaltyReferralV1ReferralEvent.queryReferringCustomerTotals(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          query: '$[0].query',
          contactIds: '$[0].contactIds',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryReferringCustomerTotalsOptions {
  /** Query to filter referring customer totals. */
  query?: CursorQuery;
  /**
   * List of contact IDs to filter referring customer totals.
   * @format GUID
   */
  contactIds?: string[];
}

/**
 * Retrieves a list of referred friend actions, given the provided paging, filtering, and sorting.
 *
 * To learn about working with Query endpoints, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
 * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/pagination),
 * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
 * @public
 * @permissionId REFERRALS.READ_REFERRAL_STATISTICS
 * @applicableIdentity APP
 * @fqn wix.loyalty.referral.tracker.v1.ReferralEvents.QueryReferredFriendActions
 */
export async function queryReferredFriendActions(
  options?: QueryReferredFriendActionsOptions
): Promise<
  NonNullablePaths<
    QueryReferredFriendActionsResponse,
    | `referredFriendActions`
    | `referredFriendActions.${number}.coupon._id`
    | `referredFriendActions.${number}.coupon.code`
    | `referredFriendActions.${number}.coupon.status`
    | `referredFriendActions.${number}.coupon.couponSpecification.minimumSubtotal`
    | `referredFriendActions.${number}.coupon.couponSpecification.name`
    | `referredFriendActions.${number}.coupon.couponSpecification.discountType`
    | `referredFriendActions.${number}.loyaltyPoints.transactionId`
    | `referredFriendActions.${number}.loyaltyPoints.amount`
    | `referredFriendActions.${number}.referredFriendId`
    | `referredFriendActions.${number}.contactId`
    | `referredFriendActions.${number}.trigger.appId`
    | `referredFriendActions.${number}.trigger.activityType`
    | `referredFriendActions.${number}.rewardType`
    | `referredFriendActions.${number}.totalActions`
    | `referredFriendActions.${number}.totalAmountSpent`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: options?.query,
    contactIds: options?.contactIds,
  });

  const reqOpts =
    ambassadorWixLoyaltyReferralV1ReferralEvent.queryReferredFriendActions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          query: '$[0].query',
          contactIds: '$[0].contactIds',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryReferredFriendActionsOptions {
  /** Query to filter referred friend actions. */
  query?: CursorQuery;
  /**
   * List of contact IDs to filter referred friend actions.
   * @format GUID
   */
  contactIds?: string[];
}
